import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { LoadingOverlay } from "@/components/ui/LoadingOverlay";
import { useModules } from "@/context/ModulesContext";
import { BarChart2, LayoutGrid, LogOut } from "lucide-react";
import { APP_SHORT_NAME, ROUTES } from "@/config/constants";
import { useAppAccess } from "@/hooks/useAppAccess";
import { ChangePasswordControl } from "@/components/auth/ChangePasswordControl";

/**
 * Layout em tela cheia para o Kanban de Suprimentos: sem menu lateral,
 * área útil da URL até a base da janela, mantendo cabeçalho e sessão.
 */
export function SuprimentosKanbanShell() {
  const { user, logout } = useAuth();
  const { can } = useAppAccess();
  const navigate = useNavigate();
  const { suprimentos } = useModules();

  return (
    <div className="flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-slate-50">
      <header className="flex-shrink-0 flex items-center justify-between h-14 px-4 md:px-5 bg-white border-b border-slate-200 shadow-sm gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
            <BarChart2 className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-slate-800 leading-tight truncate">
              {APP_SHORT_NAME} BI — Kanban de compras
            </h2>
            <p className="text-[10px] text-slate-400 truncate">Suprimentos · fluxo no período filtrado</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {can("suprimentos") && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(ROUTES.SUPRIMENTOS)}
              className="text-slate-600 hidden sm:inline-flex"
            >
              Visão geral
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.HOME)}
            className="text-slate-600 hidden sm:inline-flex"
            leftIcon={<LayoutGrid className="w-4 h-4" />}
          >
            Menu principal
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate(ROUTES.HOME)}
            className="text-slate-600 sm:hidden p-2"
            title="Menu principal"
          >
            <LayoutGrid className="w-4 h-4" />
          </Button>

          {user && (
            <div className="hidden md:flex items-center gap-1 mr-1">
              <span className="text-xs font-semibold text-slate-700 max-w-[140px] truncate">
                {user.displayName}
              </span>
              <ChangePasswordControl hidden={user.id === "dev"} />
            </div>
          )}
          <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-violet-800 font-bold text-sm select-none">
            {user?.displayName?.[0]?.toUpperCase() ?? "?"}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            leftIcon={<LogOut className="w-4 h-4" />}
            className="text-slate-500"
          >
            <span className="hidden sm:inline">Sair</span>
          </Button>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <Outlet />
      </main>

      {suprimentos.loading && <LoadingOverlay message="Carregando Suprimentos…" />}
    </div>
  );
}
